import {
  Button,
  Field,
  FormRow,
  Select,
} from "@oliasoft-open-source/react-ui-library";
import translations from 'client/internationalization/translation-map.json';
import { EntityType } from 'common/enums/entity-enums';
import {
  ArchiveQueryCondition,
  ICondition,
} from 'common/interfaces/query';
import {
  entityTableLabelMap,
  getParametersFromEntityType
} from 'common/table-viewer/utils';
import {
  conditionLabelMap,
  getAllowedConditions,
  getConditionDataTypeKey,
  getEntityMetadataOptions,
  getEntityPropertyOptions,
} from '../query-utils';
import { QueryConditionCell } from './query-condition-cell';
import { useTranslation } from 'react-i18next';

interface IQueryFormProps {
  updateCondition: (index: number, changes: Partial<ICondition>) => void;
  handleDeleteCondition: (index: number) => void;
  conditions: ICondition[];
}

const entityOptions = Object.values(EntityType).map(value => ({
  label: entityTableLabelMap[value],
  value,
}));

const getConditionOptions = (entityType: EntityType, entityPropertyName: string) => (
  getAllowedConditions(entityType, entityPropertyName).map(value => ({
    label: conditionLabelMap[value],
    value,
  }))
);

export const QueryRow = (
  {
    handleDeleteCondition,
    updateCondition,
    conditions,
  }: IQueryFormProps,
) => {
  const { t } = useTranslation();
  const queryRow = (item: ICondition, index: number) => {
    const propOptions = getEntityPropertyOptions(item.entityType);
    const metaOptions = getEntityMetadataOptions(item.entityType);

    const options = [
      { label: 'Entity Parameter', type: 'Heading' },
      ...propOptions,
      ...(metaOptions.length === 0 ? []
        : [
          { label: 'Metadata', type: 'Heading' },
          ...metaOptions,
        ]
      )];

    return (
      <FormRow key={`${getConditionDataTypeKey(item)}-${index}`}>
        <Field label={t(translations.query_entityType)}>
          <Select
            value={item.entityType}
            options={entityOptions}
            onChange={(event) => {
              const entityType = event.target.value as EntityType;
              updateCondition(index, {
                entityType,
                entityPropertyName: getParametersFromEntityType(entityType)[0],
                condition: ArchiveQueryCondition.EQ,
                conditionValue: undefined,
              });
            }}
            width={350}
          />
        </Field>
        <Field label={t(translations.query_entityParameter)}>
          <Select
            value={item.entityPropertyName}
            options={options}
            onChange={(event) => {
              updateCondition(index, {
                entityPropertyName: event.target.value,
                condition: ArchiveQueryCondition.EQ,
                conditionValue: undefined,
              });
            }}
            width={340}
          />
        </Field>
        <Field label={t(translations.query_condition)}>
          <Select
            value={item.condition}
            options={getConditionOptions(item.entityType, item.entityPropertyName)}
            name="condition"
            onChange={(event) => updateCondition(index, { [event.target.name]: event.target.value })}
            width={80}
            searchable={false}
          />
        </Field>
        <Field label={t(translations.query_conditionValue)}>
          <QueryConditionCell item={item} index={index} updateCondition={updateCondition} width={240} />
        </Field>
        <Field>
          <Button
            icon="clear"
            colored="muted"
            round
            basic
            onClick={() => handleDeleteCondition(index)}
          />
        </Field>
      </FormRow>
    );
  };

  const renderQueryRow = () => {
    return conditions.map(queryRow);
  };

  return renderQueryRow()
};
